import React from 'react';
import { Route, Switch } from 'react-router-dom';


import { RestaurantRoutesContextProvider } from 'src/shared/components/common/restaurant_routes/RestaurantRoutesContext';
import { SitesToastContainer } from 'src/shared/js/alertUtils';

import { useFlags } from 'shared/components/common/feature_flags/useFlags';
import NoMatch404 from 'shared/components/no_match_404/NoMatch404';

import { AccountPageContent, ContextualAccountPage, RestaurantlessContextualAccountPage } from 'public/components/default_template/AccountPage';
import { CheckoutPageContent, ContextualCheckoutPage } from 'public/components/default_template/CheckoutPage';
import { ConfirmationPageContent, ContextualConfirmationPage } from 'public/components/default_template/ConfirmationPage';
import RestaurantProfilePage from 'public/components/default_template/RestaurantProfilePage';
import { SiftContextProvider } from 'public/components/default_template/meta/sift/SiftContext';
import NavContent from 'public/components/default_template/nav/NavContent';
import { OrderPageContexts } from 'public/components/default_template/online_ordering/pages/OrderPageContexts';
import { RestaurantProfilePageContexts } from 'public/components/default_template/restaurant_profile/RestaurantProfilePageContexts';
import RestaurantProfilePageWrapper, { WrappedProfilePageProps } from 'public/components/default_template/restaurant_profile/RestaurantProfilePageWrapper';
import { CustomerContextProviderWrapper } from 'public/components/online_ordering/CustomerContextWrapper';
import MapPage from 'public/components/pages/map_page/MapPage';
import Nav from 'public/components/toast_local/common/nav/Nav';
import RxSearch from 'public/components/toast_local/rx_search/RxSearch';
import Splash from 'public/components/toast_local/splash_page/Splash';
import { Channel, TOAST_LOCAL_ACCOUNT_PATH, TOAST_LOCAL_CATERING_PATH, TOAST_LOCAL_ORDER_PATH, TOAST_LOCAL_PATH, TOAST_LOCAL_SEARCH_PATH } from 'public/js/siteUtilities';

import { resources } from 'config';

// Routing for www.toasttab.com/local
const ToastLocalRoutes = () => {
  const { nvsCateringDiscoveryToastLocal } = useFlags();

  const orderComponent =
    <RestaurantProfilePageWrapper respectToastLocalConfig>
      {({ restaurantProfileData, nearbyRxs }: WrappedProfilePageProps) =>
        <OrderPageContexts channel={Channel.OO}>
          <RestaurantProfilePage restaurantProfileData={restaurantProfileData} nearbyRxs={nearbyRxs} showOrderContent />
        </OrderPageContexts>}
    </RestaurantProfilePageWrapper>;

  const dineInRestaurantProfileComponent =
    <RestaurantProfilePageWrapper respectToastLocalConfig={false}>
      {({ restaurantProfileData, nearbyRxs }: WrappedProfilePageProps) =>
        <RestaurantProfilePageContexts>
          <RestaurantProfilePage restaurantProfileData={restaurantProfileData} nearbyRxs={nearbyRxs} showOrderContent={false} />
        </RestaurantProfilePageContexts>}
    </RestaurantProfilePageWrapper>;

  const checkoutComponent =
    <RestaurantProfilePageWrapper respectToastLocalConfig>
      {() =>
        <ContextualCheckoutPage>
          <CheckoutPageContent
            nav={
              <NavContent navType="stickyNav"
                logoLink={`https://${resources.toastLocalHost}/local`}
                logoSrc="/icons/toast-logo-orange.svg"
                logoLinkSameTab
                shouldShowPreviewBanner={false} />
            } />
        </ContextualCheckoutPage>}
    </RestaurantProfilePageWrapper>;

  const confirmationComponent =
    <RestaurantProfilePageWrapper respectToastLocalConfig>
      {() =>
        <ContextualConfirmationPage>
          <ConfirmationPageContent
            nav={
              <NavContent navType="stickyNav"
                logoLink={`https://${resources.toastLocalHost}/local`}
                logoSrc="/icons/toast-logo-orange.svg"
                logoLinkSameTab
                shouldShowPreviewBanner={false} />
            } />
        </ContextualConfirmationPage>}
    </RestaurantProfilePageWrapper>;

  const accountComponent =
    <RestaurantProfilePageWrapper respectToastLocalConfig={false}>
      {() =>
        <ContextualAccountPage>
          <AccountPageContent nav={
            <>
              <NavContent navType="stickyNav"
                logoLink={`https://${resources.toastLocalHost}/local`}
                logoSrc="/icons/toast-logo-orange.svg"
                logoLinkSameTab
                shouldShowPreviewBanner={false} />
              <SitesToastContainer />
            </>
          } />
        </ContextualAccountPage>}
    </RestaurantProfilePageWrapper>;

  const localAccountComponent =
    <RestaurantRoutesContextProvider homePath={TOAST_LOCAL_PATH}>
      <RestaurantlessContextualAccountPage>
        <AccountPageContent nav={
          <Nav />
        } />
      </RestaurantlessContextualAccountPage>
    </RestaurantRoutesContextProvider>;

  const splashComponent =
    <RestaurantRoutesContextProvider homePath={TOAST_LOCAL_PATH} accountPath={TOAST_LOCAL_ACCOUNT_PATH}>
      <Splash />
    </RestaurantRoutesContextProvider>;

  const rxSearchComponent =
    <RestaurantRoutesContextProvider homePath={TOAST_LOCAL_PATH} accountPath={TOAST_LOCAL_ACCOUNT_PATH}>
      <RxSearch />
    </RestaurantRoutesContextProvider>;

  const rxSearchComponentMapView =
    <RestaurantRoutesContextProvider homePath={TOAST_LOCAL_PATH} accountPath={TOAST_LOCAL_ACCOUNT_PATH} >
      <RxSearch mapView />
    </RestaurantRoutesContextProvider>;

  const rxSearchComponentCateringView =
    <RestaurantRoutesContextProvider homePath={TOAST_LOCAL_PATH} accountPath={TOAST_LOCAL_ACCOUNT_PATH} >
      <RxSearch showCatering />
    </RestaurantRoutesContextProvider>;

  return (
    <CustomerContextProviderWrapper>
      <SiftContextProvider>
        <Switch>
          <Route exact path={`${TOAST_LOCAL_ORDER_PATH}/:slug/r-:rxGuid/checkout`} key="/checkout" component={() => checkoutComponent} />
          <Route exact path={`${TOAST_LOCAL_ORDER_PATH}/:slug/r-:rxGuid/confirm`} key="/confirm" component={() => confirmationComponent} />
          <Route exact path={`${TOAST_LOCAL_ORDER_PATH}/:slug/r-:rxGuid/account`} key="/account" component={() => accountComponent} />
          <Route exact path={`${TOAST_LOCAL_ORDER_PATH}/:slug/r-:rxGuid/:itemSlug(item-[^_]+_.+)?`} key="/menuItemModalProfile" component={() => orderComponent} />
          <Route exact path={`${TOAST_LOCAL_ORDER_PATH}/:slug/r-:rxGuid/:menuSlug([^_]+_)?:menuGuid?`} key="/menuProfile" component={() => orderComponent} />
          <Route exact path={`${TOAST_LOCAL_ORDER_PATH}/:slug/r-:rxGuid`} key="/slugProfile" component={() => orderComponent} />
          <Route exact path={`${TOAST_LOCAL_PATH}/:slug/r-:rxGuid`} key="/nonOOProfile" component={() => dineInRestaurantProfileComponent} />
          <Route exact path={TOAST_LOCAL_SEARCH_PATH} key="/local/search" render={() => rxSearchComponent} />
          {/* The catering route uses the same key intentionally to prevent component re-renders */}
          {nvsCateringDiscoveryToastLocal &&
            <Route exact path={TOAST_LOCAL_CATERING_PATH} key="/local/search" render={() => rxSearchComponentCateringView} />}
          <Route exact path={`${TOAST_LOCAL_SEARCH_PATH}/map`} key="/local/search/map" render={() => rxSearchComponentMapView} />
          <Route exact path={TOAST_LOCAL_PATH} key="/local" component={() => splashComponent} />
          <Route exact path={TOAST_LOCAL_ACCOUNT_PATH} key={TOAST_LOCAL_ACCOUNT_PATH} component={() => localAccountComponent} />
          <Route exact path="/map" component={() => <MapPage color="#FF4C00" />} />
          <Route exact path="/sites-web/v1/map" component={() => <MapPage color="#FF4C00" />} />
          <Route component={NoMatch404} />
        </Switch>
      </SiftContextProvider>
    </CustomerContextProviderWrapper>
  );
};

export default ToastLocalRoutes;
